import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { SignOut, List, Heart, ChatCircle, Sun, BookOpen, User, House, Sparkle, MagnifyingGlass, Crown } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";

const NAV_LINKS = [
    { to: "/dashboard", label: "Dashboard", icon: House },
    { to: "/matches", label: "Discover", icon: Heart },
    { to: "/search", label: "Search", icon: MagnifyingGlass },
    { to: "/my-matches", label: "Matches", icon: Sparkle },
    { to: "/messages", label: "Messages", icon: ChatCircle },
    { to: "/horoscope", label: "Horoscope", icon: Sun },
    { to: "/knowledge", label: "Wisdom", icon: BookOpen },
    { to: "/profile", label: "Profile", icon: User },
];

const ADMIN_LINK = { to: "/admin", label: "Admin", icon: Crown };

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");
    const links = user?.is_admin ? [...NAV_LINKS, ADMIN_LINK] : NAV_LINKS;

    return (
        <nav className="glass fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 sm:px-6 py-3 rounded-full flex items-center gap-4 lg:gap-6 w-[min(95%,1100px)]" data-testid="navbar">
            <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2 text-gold font-serif text-xl" data-testid="nav-logo">
                <img
                    src="/logo-clean.png"
                    alt="SoulStar Dating"
                    className="h-9 w-auto rounded-md object-contain"
                />
            </Link>
            <div className="flex-1" />

            {user ? (
                <>
                    {/* Desktop links - hidden on small/medium */}
                    <div className="hidden lg:flex items-center gap-5">
                        {links.map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                className={`label-tracking transition-colors ${isActive(l.to) ? "text-gold" : "hover:text-gold"}`}
                                data-testid={`nav-${l.label.toLowerCase()}`}
                            >
                                {l.label}
                            </Link>
                        ))}
                    </div>

                    {!user.subscription_active && (
                        <Link to="/subscribe" className="btn-gold-solid hidden sm:inline-block" data-testid="nav-subscribe">Premium</Link>
                    )}

                    {/* Mobile menu trigger - visible on <lg */}
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                className="lg:hidden text-gold hover:opacity-80 transition-opacity"
                                aria-label="Menu"
                                data-testid="nav-menu-trigger"
                            >
                                <List size={24} weight="regular" />
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            side="right"
                            className="bg-[#03030A]/95 backdrop-blur-2xl border-l border-white/10 w-[280px] sm:w-[320px]"
                        >
                            <SheetHeader className="sr-only">
                                <SheetTitle>SoulStar navigation</SheetTitle>
                                <SheetDescription>Navigate the app</SheetDescription>
                            </SheetHeader>
                            <div className="flex flex-col h-full">
                                <div className="flex items-center gap-3 pb-6 border-b border-white/10">
                                    <img
                                        src="/logo-clean.png"
                                        alt=""
                                        className="h-10 w-auto rounded-md object-contain"
                                    />
                                    <div>
                                        <p className="font-serif text-lg leading-tight">{user.name}</p>
                                        <p className="text-xs text-muted">
                                            {user.sun && `☉ ${user.sun}`}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex-1 py-4 space-y-1">
                                    {links.map((l) => {
                                        const Icon = l.icon;
                                        return (
                                            <SheetClose asChild key={l.to}>
                                                <Link
                                                    to={l.to}
                                                    className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-colors ${
                                                        isActive(l.to)
                                                            ? "bg-gold/10 text-gold"
                                                            : "hover:bg-white/5 text-foreground/90"
                                                    }`}
                                                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                                                >
                                                    <Icon size={20} weight="duotone" />
                                                    <span className="font-sans">{l.label}</span>
                                                </Link>
                                            </SheetClose>
                                        );
                                    })}
                                </div>

                                <div className="border-t border-white/10 pt-4 space-y-3">
                                    {!user.subscription_active && (
                                        <SheetClose asChild>
                                            <Link to="/subscribe" className="btn-gold-solid w-full block text-center" data-testid="mobile-subscribe">
                                                Unlock Premium
                                            </Link>
                                        </SheetClose>
                                    )}
                                    <button
                                        onClick={() => { setOpen(false); handleLogout(); }}
                                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-muted hover:text-gold transition-colors text-sm"
                                        data-testid="mobile-logout"
                                    >
                                        <SignOut size={18} /> Sign Out
                                    </button>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>

                    {/* Desktop logout */}
                    <button onClick={handleLogout} className="hidden lg:inline-flex text-muted hover:text-gold transition-colors" data-testid="nav-logout" aria-label="Logout">
                        <SignOut size={20} />
                    </button>
                </>
            ) : (
                <>
                    <Link to="/knowledge" className="label-tracking hover:text-gold transition-colors hidden sm:inline" data-testid="nav-knowledge-public">Wisdom</Link>
                    <Link to="/login" className="label-tracking hover:text-gold transition-colors" data-testid="nav-login">Sign In</Link>
                    <Link to="/register" className="btn-gold-solid" data-testid="nav-register">Begin</Link>
                </>
            )}
        </nav>
    );
}
